import { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCompany } from "../hooks/useCompany";
import { useUserProfile } from "../hooks/useUserProfile";
import i18n from "../i18n";

const API_URL = process.env.REACT_APP_API_URL;
const lsKey = (userId) => `payclear_onboarding_done_${userId}`;

const inputClass =
  "w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 text-sm";

// ─── Sub-components ────────────────────────────────────────────────────────────

function Overlay({ children, onSignOut }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-7">
        {onSignOut && (
          <button
            onClick={onSignOut}
            className="absolute top-4 right-4 text-xs text-gray-400 hover:text-gray-600 transition"
          >
            Sign out
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

function StepDots({ current, total }) {
  return (
    <div className="flex justify-center gap-1.5 mb-6">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current
              ? "w-4 bg-green-400"
              : i === current
              ? "w-6 bg-green-500"
              : "w-1.5 bg-gray-200"
          }`}
        />
      ))}
    </div>
  );
}

function StepHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-5">
      <div className="w-10 h-10 rounded-xl bg-green-50 text-green-600 flex items-center justify-center mb-3">
        {icon}
      </div>
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="block mb-1 text-xs text-gray-500 font-semibold uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}

// ─── Icons ─────────────────────────────────────────────────────────────────────

function PersonIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 6h2" />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M3 10h18m-9-4V4m0 6v10M4 20h16M4 4l8-2 8 2" />
    </svg>
  );
}

function EmployeeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
    </svg>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────

export default function OnboardingWizard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companies, activeCompany, switchCompany, isInitializing, subscriptionData, refetch } = useCompany();
  const { profile: rdsProfile } = useUserProfile();

  // Local reactive state so the toggle re-renders immediately
  const [lang, setLang] = useState(i18n.language?.slice(0, 2) || "en");

  const handleLangChange = (code) => {
    i18n.changeLanguage(code);
    setLang(code);
  };

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  // Steps already complete when the wizard opened (a name and companies from the person's Olene
  // account come in before it shows): skipped, so there's less to type.
  const [skip, setSkip] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [profile, setProfile] = useState({ first_name: "", last_name: "", middle_initial: "" });
  const [company, setCompany] = useState({
    name: "", dba: "", ein: "", address: "", city: "", state: "", zip: "", phone: "", email: "",
  });
  const [bank, setBank] = useState({
    bank_name: "", routing_number: "", account_number: "",
  });
  const [emp, setEmp] = useState({
    first_name: "", last_name: "", wage: "", is_salary: false, salary_period: "weekly",
  });

  const userId = auth.user?.profile?.sub;

  const handleSignOut = () => {
    auth.signOut();
  };

  // Show wizard only for users who haven't completed setup.
  // Before showing, check if the account is already fully set up (name + company + employees).
  // If so, silently mark onboarding done and skip the wizard entirely.
  useEffect(() => {
    if (visible) return; // decided (and which steps to skip frozen) once it shows
    if (isInitializing) return;
    if (!auth.isAuthenticated || !userId) return;
    if (localStorage.getItem(lsKey(userId))) return;

    // Wait for async data to settle before making any decision
    if (!subscriptionData) return;
    if (subscriptionData.is_admin) return;
    if (activeCompany?.shared) return; // a teammate in someone else's company has nothing to set up
    if (!rdsProfile) return; // useUserProfile loads separately from useCompany

    const token = auth.user?.access_token;

    const profileComplete =
      rdsProfile.first_name?.trim() && rdsProfile.last_name?.trim();
    setSkip({
      1: !!profileComplete,
      2: !!(activeCompany?.name?.trim() && activeCompany?.address?.trim()),
      3: !!(activeCompany?.routing_number && activeCompany?.account_number),
    });

    if (profileComplete && activeCompany) {
      // Profile + company exist — check if they also have at least one employee
      fetch(`${API_URL}/employees?company_id=${activeCompany.company_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : []))
        .then((employees) => {
          if (employees.length > 0) {
            // Fully set up — auto-dismiss and never show again
            localStorage.setItem(lsKey(userId), "1");
          } else {
            setVisible(true);
          }
        })
        .catch(() => setVisible(true));
      return;
    }

    setVisible(true);
  }, [visible, isInitializing, auth.isAuthenticated, auth.user, userId, subscriptionData, rdsProfile, activeCompany]);

  // Pre-fill profile from RDS + OIDC
  useEffect(() => {
    if (!rdsProfile) return;
    const parts = (auth.user?.profile?.name || "").trim().split(" ");
    setProfile({
      first_name:     rdsProfile.first_name     || parts[0]                                  || "",
      last_name:      rdsProfile.last_name       || (parts.length >= 2 ? parts[parts.length - 1] : ""),
      middle_initial: rdsProfile.middle_initial  || "",
    });
  }, [rdsProfile, auth.user]);

  // Pre-fill company + bank from active company (if one already exists)
  useEffect(() => {
    if (!activeCompany) return;
    setCompany({
      name:    activeCompany.name    || "",
      dba:     activeCompany.dba     || "",
      ein:     activeCompany.ein     || "",
      address: activeCompany.address || "",
      city:    activeCompany.city    || "",
      state:   activeCompany.state   || "",
      zip:     activeCompany.zip     || "",
      phone:   activeCompany.phone   || "",
      email:   activeCompany.email   || "",
    });
    setBank({
      bank_name:      activeCompany.bank_name      || "",
      routing_number: activeCompany.routing_number || "",
      account_number: activeCompany.account_number || "",
    });
  }, [activeCompany]);

  const back = () => {
    setError("");
    setStep((s) => { let n = s - 1; while (n > 0 && skip[n]) n--; return n; });
  };

  const finish = () => {
    if (userId) localStorage.setItem(lsKey(userId), "1");
    setVisible(false);
  };

  const next = () => {
    setError("");
    setStep((s) => { let n = s + 1; while (skip[n]) n++; return n; });
  };

  const token = auth.user?.access_token;
  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const handleSaveProfile = async () => {
    if (!profile.first_name.trim() || !profile.last_name.trim()) {
      setError("First name and last name are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await fetch(`${API_URL}/user/profile`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({
          first_name:     profile.first_name.trim(),
          last_name:      profile.last_name.trim(),
          middle_initial: profile.middle_initial.trim().toUpperCase() || "",
          language:       i18n.language || "en",
        }),
      });
    } catch {}
    setSaving(false);
    next();
  };

  const handleSaveCompany = async () => {
    if (!company.name.trim()) {
      setError("Company name is required.");
      return;
    }
    if (company.dba.trim().length > 120) { setError(t("invalid_dba_msg")); return; }
    if (company.ein.trim() && !/^\d{2}-?\d{7}$/.test(company.ein.trim())) { setError(t("invalid_ein_msg")); return; }
    setSaving(true);
    setError("");
    try {
      if (activeCompany) {
        // Update existing company
        await fetch(`${API_URL}/companies/${activeCompany.company_id}`, {
          method: "PUT",
          headers: authHeaders,
          body: JSON.stringify({
            ...activeCompany,
            ...company,
            state: company.state.toUpperCase(),
          }),
        });
      } else {
        // Create first company for this user
        const res = await fetch(`${API_URL}/companies`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            name:    company.name.trim(),
            dba:     company.dba.trim(),
            ein:     company.ein.trim(),
            address: company.address.trim(),
            city:    company.city.trim(),
            state:   company.state.trim().toUpperCase(),
            zip:     company.zip.trim(),
            phone:   company.phone.trim(),
            email:   company.email.trim(),
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body?.detail?.message || "Failed to create company. Please try again.");
          setSaving(false);
          return;
        }
      }
      await refetch();
    } catch {
      setError("Network error. Please try again.");
      setSaving(false);
      return;
    }
    setSaving(false);
    next();
  };

  const handleSaveBank = async () => {
    // Bank details are optional — skip silently if no company somehow
    if (!activeCompany) { next(); return; }
    setSaving(true);
    try {
      await fetch(`${API_URL}/companies/${activeCompany.company_id}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({
          ...activeCompany,
          ...company,
          ...bank,
          state: (activeCompany.state || company.state || "").toUpperCase(),
        }),
      });
      await refetch();
    } catch {}
    setSaving(false);
    next();
  };

  const handleSaveEmployee = async () => {
    if (!emp.first_name.trim() || !emp.last_name.trim()) {
      // Skip employee — it's optional
      next();
      return;
    }
    // Need a company at this point (created in step 2)
    const company_id = activeCompany?.company_id;
    if (!company_id) { next(); return; }
    setSaving(true);
    try {
      await fetch(`${API_URL}/employees`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          company_id,
          first_name:    emp.first_name.trim(),
          last_name:     emp.last_name.trim(),
          address:       "",
          wage:          emp.wage !== "" ? Number(emp.wage) : 0.0,
          contact_info:  { email: "", phone: "" },
          is_salary:     emp.is_salary,
          salary_period: emp.is_salary ? emp.salary_period : "weekly",
        }),
      });
    } catch {}
    setSaving(false);
    next();
  };

  if (!visible) return null;

  const TOTAL = 4; // progress dots for steps 1–4
  const ownCompanies = (companies || []).filter((c) => !c.shared);

  // ── Step 0: Welcome ──────────────────────────────────────────────────────────
  if (step === 0) {
    return (
      <Overlay onSignOut={handleSignOut}>
        <div className="text-center py-2">
          <div className="text-5xl mb-4">👋</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("wiz_welcome_title")}</h2>
          <p className="text-gray-500 text-sm mb-8 max-w-xs mx-auto leading-relaxed">
            {t("wiz_welcome_body")}
          </p>
          <button
            onClick={next}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-xl transition text-sm"
          >
            {t("wiz_get_started")}
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Step 1: Profile ──────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <Overlay onSignOut={handleSignOut}>
        <StepDots current={0} total={TOTAL} />
        <StepHeader
          icon={<PersonIcon />}
          title={t("wiz_profile_title")}
          subtitle={t("wiz_profile_subtitle")}
        />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("wiz_first_name")}>
              <input
                className={inputClass}
                value={profile.first_name}
                onChange={(e) => { setError(""); setProfile((p) => ({ ...p, first_name: e.target.value })); }}
                placeholder="Jane"
              />
            </Field>
            <Field label={t("wiz_last_name")}>
              <input
                className={inputClass}
                value={profile.last_name}
                onChange={(e) => { setError(""); setProfile((p) => ({ ...p, last_name: e.target.value })); }}
                placeholder="Smith"
              />
            </Field>
          </div>
          <Field label={t("wiz_middle_initial")} className="w-40">
            <input
              className={inputClass}
              maxLength={1}
              value={profile.middle_initial}
              onChange={(e) => setProfile((p) => ({ ...p, middle_initial: e.target.value }))}
              placeholder="A"
            />
          </Field>

          {/* Language preference */}
          <div>
            <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1.5">
              {t("wiz_language_label")}
            </p>
            <div className="flex gap-2">
              {[
                { code: "en", label: "English" },
                { code: "es", label: "Español" },
              ].map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => handleLangChange(code)}
                  className={`flex-1 py-2 text-sm font-medium rounded-xl border transition ${
                    lang === code
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-white text-gray-600 border-gray-300 hover:border-green-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <div className="mt-6 flex gap-2">
          <button
            onClick={back}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
          >
            {t("wiz_back")}
          </button>
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition text-sm"
          >
            {saving ? t("saving") : t("wiz_save_continue")}
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Step 2: Company Info (required) ─────────────────────────────────────────
  if (step === 2) {
    return (
      <Overlay onSignOut={handleSignOut}>
        <StepDots current={1} total={TOTAL} />
        <StepHeader
          icon={<BuildingIcon />}
          title={t("wiz_company_title")}
          subtitle={t("wiz_company_subtitle")}
        />
        <div className="space-y-3">
          {ownCompanies.length > 1 && (
            <Field label={t("wiz_pick_company")}>
              <select
                className={inputClass}
                value={activeCompany?.company_id || ""}
                onChange={(e) => { setError(""); switchCompany(e.target.value); }}
              >
                {ownCompanies.map((c) => (
                  <option key={c.company_id} value={c.company_id}>{c.name}</option>
                ))}
              </select>
            </Field>
          )}
          <Field label={t("wiz_company_name")}>
            <input
              className={inputClass}
              value={company.name}
              onChange={(e) => { setError(""); setCompany((c) => ({ ...c, name: e.target.value })); }}
              placeholder="Acme Landscaping LLC"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("company_dba")}>
              <input
                className={inputClass}
                value={company.dba}
                onChange={(e) => { setError(""); setCompany((c) => ({ ...c, dba: e.target.value })); }}
              />
            </Field>
            <Field label={t("company_ein")}>
              <input
                className={inputClass}
                value={company.ein}
                onChange={(e) => { setError(""); setCompany((c) => ({ ...c, ein: e.target.value })); }}
                placeholder="12-3456789"
              />
            </Field>
          </div>
          <Field label={t("wiz_address")}>
            <input
              className={inputClass}
              value={company.address}
              onChange={(e) => setCompany((c) => ({ ...c, address: e.target.value }))}
              placeholder="123 Main St"
            />
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label={t("wiz_city")} className="col-span-1">
              <input
                className={inputClass}
                value={company.city}
                onChange={(e) => setCompany((c) => ({ ...c, city: e.target.value }))}
                placeholder="Austin"
              />
            </Field>
            <Field label={t("wiz_state")}>
              <input
                className={inputClass}
                maxLength={2}
                value={company.state}
                onChange={(e) => setCompany((c) => ({ ...c, state: e.target.value }))}
                placeholder="TX"
              />
            </Field>
            <Field label={t("wiz_zip")}>
              <input
                className={inputClass}
                value={company.zip}
                onChange={(e) => setCompany((c) => ({ ...c, zip: e.target.value }))}
                placeholder="78701"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("wiz_phone")}>
              <input
                className={inputClass}
                value={company.phone}
                onChange={(e) => setCompany((c) => ({ ...c, phone: e.target.value }))}
                placeholder="(512) 555-0100"
              />
            </Field>
            <Field label={t("wiz_email")}>
              <input
                className={inputClass}
                type="email"
                value={company.email}
                onChange={(e) => setCompany((c) => ({ ...c, email: e.target.value }))}
                placeholder="hello@acme.com"
              />
            </Field>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <div className="mt-6 flex gap-2">
          <button
            onClick={back}
            disabled={saving}
            className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
          >
            {t("wiz_back")}
          </button>
          <button
            onClick={handleSaveCompany}
            disabled={saving}
            className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition text-sm"
          >
            {saving ? t("saving") : t("wiz_save_continue")}
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Step 3: Bank Details ─────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <Overlay onSignOut={handleSignOut}>
        <StepDots current={2} total={TOTAL} />
        <StepHeader
          icon={<BankIcon />}
          title={t("manual_bank_title")}
          subtitle={t("manual_bank_hint")}
        />
        <div className="space-y-3">
          <Field label={t("wiz_bank_name")}>
            <input
              className={inputClass}
              value={bank.bank_name}
              onChange={(e) => setBank((b) => ({ ...b, bank_name: e.target.value }))}
              placeholder="First National Bank"
            />
          </Field>
          <Field label={t("wiz_routing")}>
            <input
              className={inputClass}
              value={bank.routing_number}
              maxLength={9}
              onChange={(e) => setBank((b) => ({ ...b, routing_number: e.target.value }))}
              placeholder="9-digit routing number"
            />
          </Field>
          <Field label={t("wiz_account")}>
            <input
              className={inputClass}
              value={bank.account_number}
              maxLength={17}
              onChange={(e) => setBank((b) => ({ ...b, account_number: e.target.value }))}
              placeholder="Bank account number"
            />
          </Field>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mt-1">
            <div className="flex gap-2.5">
              <svg className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">{t("wiz_bank_why_title")}</p>
                <p className="text-xs text-blue-600 leading-relaxed">{t("wiz_bank_why_body")}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 space-y-2">
          <div className="flex gap-2">
            <button
              onClick={back}
              disabled={saving}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
            >
              {t("wiz_back")}
            </button>
            <button
              onClick={handleSaveBank}
              disabled={saving}
              className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition text-sm"
            >
              {saving ? t("saving") : t("wiz_save_continue")}
            </button>
          </div>
          <button
            onClick={next}
            disabled={saving}
            className="w-full text-xs text-gray-400 hover:text-gray-600 transition py-1"
          >
            {t("wiz_skip")}
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Step 4: First Employee ───────────────────────────────────────────────────
  if (step === 4) {
    return (
      <Overlay onSignOut={handleSignOut}>
        <StepDots current={3} total={TOTAL} />
        <StepHeader
          icon={<EmployeeIcon />}
          title={t("wiz_employee_title")}
          subtitle={t("wiz_employee_subtitle")}
        />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label={t("wiz_emp_first")}>
              <input
                className={inputClass}
                value={emp.first_name}
                onChange={(e) => setEmp((p) => ({ ...p, first_name: e.target.value }))}
                placeholder="John"
              />
            </Field>
            <Field label={t("wiz_emp_last")}>
              <input
                className={inputClass}
                value={emp.last_name}
                onChange={(e) => setEmp((p) => ({ ...p, last_name: e.target.value }))}
                placeholder="Doe"
              />
            </Field>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <input
              type="checkbox"
              id="wiz-is-salary"
              checked={emp.is_salary}
              onChange={(e) => setEmp((p) => ({ ...p, is_salary: e.target.checked, salary_period: "weekly" }))}
              className="rounded"
            />
            <label htmlFor="wiz-is-salary" className="text-xs text-gray-600 font-medium">{t("wiz_salaried")}</label>
          </div>
          {emp.is_salary && (
            <div className="flex gap-1 mb-2">
              {[
                { key: "weekly",  label: t("wiz_weekly")  },
                { key: "monthly", label: t("wiz_monthly") },
                { key: "yearly",  label: t("wiz_yearly")  },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setEmp((prev) => ({ ...prev, salary_period: key }))}
                  className={`flex-1 py-1.5 text-xs font-medium rounded-lg border transition ${
                    emp.salary_period === key
                      ? "bg-green-500 text-white border-green-500"
                      : "bg-white text-gray-600 border-gray-300 hover:border-green-400"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          <Field label={
            !emp.is_salary ? t("wiz_hourly_wage") :
            emp.salary_period === "yearly"  ? t("wiz_annual_salary")  :
            emp.salary_period === "monthly" ? t("wiz_monthly_salary") :
            t("wiz_weekly_salary")
          }>
            <input
              className={inputClass}
              type="number"
              min="0"
              step="0.01"
              value={emp.wage}
              onChange={(e) => setEmp((p) => ({ ...p, wage: e.target.value }))}
              placeholder={
                !emp.is_salary ? "18.00" :
                emp.salary_period === "yearly" ? "52000" :
                emp.salary_period === "monthly" ? "4333" :
                "1000"
              }
            />
          </Field>
        </div>
        <div className="mt-6 space-y-2">
          <div className="flex gap-2">
            <button
              onClick={back}
              disabled={saving}
              className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-500 hover:bg-gray-50 transition"
            >
              {t("wiz_back")}
            </button>
            <button
              onClick={handleSaveEmployee}
              disabled={saving}
              className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition text-sm"
            >
              {saving ? t("saving") : t("wiz_add_finish")}
            </button>
          </div>
          <button
            onClick={() => setStep(5)}
            disabled={saving}
            className="w-full text-xs text-gray-400 hover:text-gray-600 transition py-1"
          >
            {t("wiz_skip_employee")}
          </button>
        </div>
      </Overlay>
    );
  }

  // ── Step 5: Done ─────────────────────────────────────────────────────────────
  return (
    <Overlay onSignOut={handleSignOut}>
      <div className="text-center py-2">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-green-500"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t("wiz_done_title")}</h2>
        <p className="text-gray-500 text-sm mb-8 max-w-xs mx-auto leading-relaxed">
          {t("wiz_done_body")}
        </p>
        <button
          onClick={() => { finish(); navigate("/"); }}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-xl transition text-sm"
        >
          {t("wiz_go_dashboard")}
        </button>
      </div>
    </Overlay>
  );
}
